// src/armHunt.ts
import { pendingBrandRequestOf } from "@rock/types";

// src/selectors.ts
import {
  APP_VISIBLE_TAGS,
  DOC_TAG_LABELS,
  ModelDocTagSchema
} from "@rock/types";
function documentLinks(model) {
  return Object.values(model?.documents ?? {}).filter((link) => !!link);
}
function linkedDocumentIds(model) {
  return documentLinks(model).map((link) => link.id);
}
function newlyLinkedDocumentIds(before, after) {
  const previous = new Set(before ? linkedDocumentIds(before) : []);
  return linkedDocumentIds(after).filter((id) => !previous.has(id));
}
function documentEntries(model, docs) {
  const byId = /* @__PURE__ */ new Map();
  for (const doc of docs) {
    if (doc) byId.set(doc.id, doc);
  }
  return documentLinks(model).flatMap((link) => {
    const doc = byId.get(link.id);
    return doc ? [{ link, doc }] : [];
  });
}
var TAG_ORDER = Object.fromEntries(
  ModelDocTagSchema.options.map((tag, index) => [tag, index])
);
function primaryTag(doc) {
  return doc.tags[0] ?? "owner-manual";
}
function includesAppVisibleTag(tags) {
  return tags.some((tag) => APP_VISIBLE_TAGS.includes(tag));
}
function isAppVisibleDocument(doc) {
  return includesAppVisibleTag(doc.tags);
}
function isDirectLink(link) {
  return link.via === "direct";
}
function isSupersededEntry(entry) {
  return !!entry.link.supersededBy;
}
function libraryDocuments(entries) {
  return [...entries].sort(
    (a, b) => TAG_ORDER[primaryTag(a.doc)] - TAG_ORDER[primaryTag(b.doc)] || Number(isSupersededEntry(a)) - Number(isSupersededEntry(b)) || a.link.linkedAt.localeCompare(b.link.linkedAt) || a.doc.id.localeCompare(b.doc.id)
  );
}
function appVisibleDocuments(entries) {
  return libraryDocuments(entries).filter((entry) => isAppVisibleDocument(entry.doc) && !isSupersededEntry(entry));
}
function hasAppVisibleDoc(entries) {
  return entries.some((entry) => isAppVisibleDocument(entry.doc) && !isSupersededEntry(entry));
}
function documentsTagged(entries, tag) {
  return entries.filter((entry) => !!entry.doc.tags?.includes(tag) && !isSupersededEntry(entry)).sort((a, b) => b.link.linkedAt.localeCompare(a.link.linkedAt) || a.doc.id.localeCompare(b.doc.id));
}
function primaryDocumentTagged(entries, tag) {
  return documentsTagged(entries, tag)[0];
}
function documentTitle(doc) {
  return doc.title || DOC_TAG_LABELS[primaryTag(doc)];
}
function ownerManualDocument(entries) {
  return primaryDocumentTagged(entries, "owner-manual");
}
function manualUrl(entries) {
  return ownerManualDocument(entries)?.doc.storageUrl || void 0;
}
function hasManual(entries) {
  return !!manualUrl(entries);
}
function isManualConfirmed(entries) {
  return hasManual(entries);
}

// src/armHunt.ts
function ambientHuntBlockedReason({ model, entries, job }) {
  if (model.archivedAt) return "model is archived";
  if (model.parked) return `model is parked (${model.parked.reason})`;
  if (hasManual(entries)) return "already has a published owner manual";
  if (job) return `job already ${job.status}`;
  if (!model.identity?.concludedAt) return "identity has not concluded \u2014 waiting for brand resolution";
  const requestId = pendingBrandRequestOf(model);
  if (requestId) return `brand request ${requestId} is pending \u2014 waiting for the catalog to name the brand`;
  return void 0;
}

// src/autoConfirm.ts
import {
  isBrandOwnedHost,
  isBrandTrustedHost,
  isHostWithin,
  normalizeBrandDomain
} from "@rock/brands";
import { normalizeIdentifier } from "@rock/util";
var GATE_REVISION = 3;
var AUTO_CONFIRM_GATE_MIN_CONFIDENCE = 0.9;
var TRUSTED_DOCUMENT_HOSTS = [];
var NORMALIZED_TRUSTED_HOSTS = TRUSTED_DOCUMENT_HOSTS.map(normalizeBrandDomain).filter(
  (host) => !!host
);
function evaluateAutoConfirmGate(input, now) {
  const checks = {
    publisher: publisherCheck(input.verdict),
    domainTier: domainTierCheck(input.urls, input.linkedFromUrls ?? [], input.brand),
    identityStrength: identityStrengthCheck(input.verdict, input.claimedModel),
    confidence: confidenceCheck(input.verdict)
  };
  return {
    pass: Object.values(checks).every((check) => check.pass),
    gateRevision: GATE_REVISION,
    evaluatedAt: now,
    checks
  };
}
function publisherCheck(verdict) {
  if (verdict.publisher === "manufacturer") return { pass: true, detail: "manufacturer-authored" };
  return { pass: false, detail: verdict.publisher ?? "publisher absent (pre-v5 verdict)" };
}
function domainTierCheck(urls, linkedFromUrls, brand) {
  const served = tierHit(urls, brand);
  if (served) return { pass: true, detail: served };
  const linked = tierHit(linkedFromUrls, brand);
  if (linked) return { pass: true, detail: `linking page: ${linked}` };
  return {
    pass: false,
    detail: brand ? `no brand-owned or trusted host among ${urls.length} URL(s) + ${linkedFromUrls.length} linking page(s)` : `no linked brand to own a host; no trusted host among ${urls.length} URL(s)`
  };
}
function tierHit(urls, brand) {
  const hosts = urls.map(normalizeBrandDomain).filter((host) => !!host);
  const owned = brand && hosts.find((host) => isBrandOwnedHost(brand, host));
  if (owned) return `${owned} is brand-owned`;
  const trusted = brand && hosts.find((host) => isBrandTrustedHost(brand, host));
  if (trusted) return `${trusted} is trusted for this brand`;
  const global = hosts.find((host) => isHostWithin(host, NORMALIZED_TRUSTED_HOSTS));
  return global && `${global} is a trusted document host`;
}
function identityStrengthCheck(verdict, claimedModel) {
  const target = normalizeIdentifier(claimedModel);
  if (!target) return { pass: false, detail: "no claimed model to match" };
  if (verdict.literalHits) {
    const hit = verdict.literalHits.some((entry) => normalizeIdentifier(entry) === target);
    return hit ? { pass: true, detail: "claimed model literally printed in the document" } : { pass: false, detail: "document scan found no literal hit for the claimed model" };
  }
  if ((verdict.coversModels ?? []).some((entry) => normalizeIdentifier(entry) === target)) {
    return { pass: true, detail: "exact covers-models entry (pre-gate verdict, no scan recorded)" };
  }
  return { pass: false, detail: "no literal evidence for the claimed model (range/family evidence only)" };
}
function confidenceCheck(verdict) {
  const pass = verdict.confidence >= AUTO_CONFIRM_GATE_MIN_CONFIDENCE;
  return { pass, detail: `${verdict.confidence} vs the ${AUTO_CONFIRM_GATE_MIN_CONFIDENCE} auto-confirm bar` };
}

// src/candidates.ts
import {
  ModelDocTagSchema as ModelDocTagSchema2
} from "@rock/types";

// src/storagePath.ts
var DEV_DOCS_BASE_URL = "https://dev-docs.rockhome.app";
var DOCS_BASE_URL_BY_PROJECT = {
  "rock-app-production": "https://docs.rockhome.app",
  "rock-app-dev": DEV_DOCS_BASE_URL
};
var DOCS_HOSTS = Object.values(DOCS_BASE_URL_BY_PROJECT).map((url) => new URL(url).host);
function docsBaseUrlForProject(projectId) {
  return (projectId ? DOCS_BASE_URL_BY_PROJECT[projectId] : void 0) ?? DEV_DOCS_BASE_URL;
}
function docsBucketNameForProject(projectId) {
  return `${projectId}-docs`;
}
function candidateStoragePath(modelId, candidateId) {
  return `models/${modelId}/candidates/${candidateId}.pdf`;
}
function offerStoragePath(modelId, contentId) {
  return `models/${modelId}/offers/${contentId}.pdf`;
}
function trimmedStoragePath(modelId, docId, contentId) {
  return `models/${modelId}/trimmed/${docId}_${contentId}.pdf`;
}
function isUploadedSource(sourceUrl) {
  const path = sourceUrl ? storagePathFromUrl(sourceUrl) : void 0;
  return !!path && /^models\/[^/]+\/offers\//.test(path);
}
function assertModelStoragePath(path) {
  if (!path.startsWith("models/") || path.includes("..") || path.includes("//") || /\s/.test(path)) {
    throw new Error(`document storagePath not allowed: ${path}`);
  }
}
function storagePathFromUrl(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return void 0;
  }
  const path = DOCS_HOSTS.includes(parsed.host) ? decodeURIComponent(parsed.pathname.slice(1)) : decodeURIComponent(url.match(/\/o\/([^?]+)/)?.[1] ?? "");
  return path.startsWith("models/") ? path : void 0;
}
function documentStoragePath(doc) {
  return doc.storagePath ?? storagePathFromUrl(doc.storageUrl);
}
function servedDocumentUrl(docsBaseUrl, path) {
  return `${docsBaseUrl}/${path}`;
}
function planDocumentMirror(before, after, docsBaseUrl) {
  const previousPath = before ? documentStoragePath(before) : void 0;
  if (after) {
    if (after.storageUrl?.startsWith(`${docsBaseUrl}/`)) return {};
    const path = documentStoragePath(after);
    if (!path) return {};
    const abandoned = previousPath && previousPath !== path ? { deletePath: previousPath } : {};
    return { mirror: { path, storageUrl: servedDocumentUrl(docsBaseUrl, path) }, ...abandoned };
  }
  return previousPath ? { deletePath: previousPath } : {};
}

// src/trim.ts
var TRIM_REVISION = 2;
var TRIM_ENGINE = "cld3";
var TRIM_MIN_LETTERS = 120;
var TRIM_MIN_PROB = 0.9;
var TRIM_MAX_ENGLISH_SECTIONS = 3;
var ENGLISH = "en";
function classifyTrimPage(page) {
  if (page.letters < TRIM_MIN_LETTERS || !page.reliable) return "unknown";
  if (page.lang !== ENGLISH) return "other";
  return page.prob >= TRIM_MIN_PROB ? "en" : "unknown";
}
function computeTrimProposal(pages) {
  const total = pages.length;
  const classes = classifyPages(pages);
  const classified = pages.filter((_, i) => classes[i] !== "unknown");
  const unknownFraction = total === 0 ? 1 : round3((total - classified.length) / total);
  const confidence = classified.length ? round3(Math.min(...classified.map((page) => page.prob))) : 0;
  const declined = (reason) => ({
    outcome: "declined",
    keepRanges: [],
    keptPages: 0,
    reason,
    confidence,
    unknownFraction,
    cautions: []
  });
  if (total === 0) return declined("empty");
  if (classified.length === 0) return declined("no-classifiable-text");
  if (!classes.includes("en")) return declined("no-english");
  const attached = attachUnknowns(classes);
  const segments = toSegments(attached);
  const englishAt = segments.flatMap((segment, i) => segment.cls === "en" ? [i] : []);
  if (englishAt.length > TRIM_MAX_ENGLISH_SECTIONS) return declined("interleaved");
  const cautions = [];
  if (englishAt.some((i) => i !== 0 && i !== segments.length - 1)) cautions.push("english-in-middle");
  if (uncertainPagesOf(pages, attached).length > 0) cautions.push("uncertain-pages");
  const keepRanges = segments.filter((segment) => segment.cls === "en").map(({ start, end }) => ({ start, end }));
  const keptPages = countPagesInRanges(keepRanges);
  const outcome = keptPages === total ? "none" : "trim";
  return { outcome, keepRanges, keptPages, confidence, unknownFraction, cautions };
}
function uncertainTrimPages(pages) {
  const classes = classifyPages(pages);
  if (classes.every((cls) => cls === "unknown")) return [];
  return uncertainPagesOf(pages, attachUnknowns(classes));
}
function buildDocumentLanguageAnalysis(pages, now) {
  const rounded = pages.map((page) => ({ ...page, prob: round3(page.prob) }));
  return {
    revision: TRIM_REVISION,
    engine: TRIM_ENGINE,
    analyzedAt: now,
    totalPages: rounded.length,
    pages: rounded,
    proposal: computeTrimProposal(rounded)
  };
}
function appliedTrimDecision(analysis, appliedRanges, now) {
  const matches = rangesEqual(analysis.proposal.keepRanges, appliedRanges);
  return {
    action: matches ? "applied" : "applied-adjusted",
    appliedRanges,
    analysisRevision: analysis.revision,
    decidedAt: now
  };
}
function summarizeLanguagePages(analysis) {
  const counts = {};
  for (const page of analysis.pages) {
    const key = classifyTrimPage(page) === "unknown" ? "unknown" : page.lang;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}
function assertCanTrimDocument(entries, docId) {
  const entry = entries.find((candidate) => candidate.doc.id === docId);
  if (!entry) throw new Error(`document ${docId} is not confirmed on this model`);
  if (entry.link.supersededBy) throw new Error("this document is already superseded \u2014 cut the current one");
  assertTrimHasSource(entry.doc.trim);
  if (!documentStoragePath(entry.doc)) {
    throw new Error("this document has no stored copy to cut from");
  }
  return entry;
}
function assertTrimHasSource(trim) {
  if (trim && !trim.sourcePath) {
    throw new Error("this pdf was trimmed under an older format \u2014 migrate the record before trimming it again");
  }
}
function assertValidPageRanges(ranges, totalPages) {
  if (ranges.length === 0) throw new Error("give at least one page range");
  let previousEnd = 0;
  for (const { start, end } of ranges) {
    if (!Number.isInteger(start) || !Number.isInteger(end)) throw new Error("page numbers must be whole numbers");
    if (start < 1) throw new Error("pages start at 1");
    if (totalPages !== void 0 && end > totalPages) throw new Error(`pages must be between 1 and ${totalPages}`);
    if (end < start) throw new Error(`range ${start}-${end} is backwards`);
    if (start <= previousEnd) throw new Error("ranges must be ascending and non-overlapping");
    previousEnd = end;
  }
}
function resolveTrimKeepRanges(selection, totalPages) {
  assertValidPageRanges(selection.ranges, totalPages);
  const keepRanges = selection.mode === "keep" ? selection.ranges : invertPageRanges(selection.ranges, totalPages);
  if (keepRanges.length === 0) throw new Error("that removes every page \u2014 nothing would be left");
  if (countPagesInRanges(keepRanges) >= totalPages) throw new Error("that keeps every page \u2014 nothing to cut");
  return keepRanges;
}
function composeKeepRanges(existing, next) {
  const sourcePages = pagesInRanges(existing);
  assertValidPageRanges(next, sourcePages.length);
  return collapseToRanges(
    pagesInRanges(next).map((page) => {
      const sourcePage = sourcePages[page - 1];
      if (sourcePage === void 0) throw new Error(`page ${page} is outside the current pdf`);
      return sourcePage;
    })
  );
}
function collapseToRanges(pages) {
  const ranges = [];
  for (const page of pages) {
    const last = ranges[ranges.length - 1];
    if (last && page === last.end + 1) last.end = page;
    else ranges.push({ start: page, end: page });
  }
  return ranges;
}
function invertPageRanges(ranges, totalPages) {
  const kept = [];
  let next = 1;
  for (const { start, end } of ranges) {
    if (start > next) kept.push({ start: next, end: start - 1 });
    next = Math.max(next, end + 1);
  }
  if (next <= totalPages) kept.push({ start: next, end: totalPages });
  return kept;
}
function formatPageRanges(ranges) {
  return ranges.map(({ start, end }) => start === end ? `${start}` : `${start}-${end}`).join(", ");
}
function parsePageRanges(input, totalPages) {
  const parts = input.split(",").map((part) => part.trim()).filter(Boolean);
  const ranges = parts.map((part) => {
    const match = part.match(/^(\d+)\s*[-–]\s*(\d+)$|^(\d+)$/);
    if (!match) throw new Error(`"${part}" is not a page or page range (e.g. "5" or "1-39")`);
    const start = Number(match[1] ?? match[3]);
    const end = Number(match[2] ?? match[3]);
    return { start, end };
  });
  assertValidPageRanges(ranges, totalPages);
  return ranges;
}
function pagesInRanges(ranges) {
  return ranges.flatMap(({ start, end }) => Array.from({ length: end - start + 1 }, (_, i) => start + i));
}
function countPagesInRanges(ranges) {
  return ranges.reduce((sum, { start, end }) => sum + (end - start + 1), 0);
}
function rangesEqual(a, b) {
  return a.length === b.length && a.every((range, i) => range.start === b[i]?.start && range.end === b[i]?.end);
}
function classifyPages(pages) {
  return absorbLoneWeakReads(pages, pages.map(classifyTrimPage));
}
function absorbLoneWeakReads(pages, classes) {
  return classes.map((cls, i) => {
    if (cls !== "other" || (pages[i]?.prob ?? 0) >= TRIM_MIN_PROB) return cls;
    const neighbors = [nearestClassified(classes, i, -1), nearestClassified(classes, i, 1)].filter(
      (neighbor) => neighbor !== void 0
    );
    return neighbors.length > 0 && neighbors.every((neighbor) => neighbor === "en") ? "unknown" : cls;
  });
}
function nearestClassified(classes, from, step) {
  for (let i = from + step; i >= 0 && i < classes.length; i += step) {
    if (classes[i] !== "unknown") return classes[i];
  }
  return void 0;
}
function uncertainPagesOf(pages, attached) {
  return pages.flatMap((page, i) => isUncertainProse(page) && couldMoveTheCut(page, i, attached) ? [i + 1] : []);
}
function isUncertainProse(page) {
  return page.letters >= TRIM_MIN_LETTERS && (!page.reliable || page.prob < TRIM_MIN_PROB);
}
function couldMoveTheCut(page, i, attached) {
  const landed = attached[i];
  const read = page.lang === ENGLISH ? "en" : "other";
  const bordersAnother = [attached[i - 1], attached[i + 1]].some((next) => next !== void 0 && next !== landed);
  return read !== landed || bordersAnother;
}
function attachUnknowns(classes) {
  const attached = new Array(classes.length);
  let previous;
  const backfill = [];
  for (let i = 0; i < classes.length; i++) {
    const cls = classes[i];
    if (cls === "en" || cls === "other") {
      for (const j of backfill) attached[j] = cls;
      backfill.length = 0;
      attached[i] = cls;
      previous = cls;
    } else {
      backfill.push(i);
    }
  }
  for (const j of backfill) {
    attached[j] = previous ?? "en";
  }
  return attached;
}
function toSegments(attached) {
  const segments = [];
  attached.forEach((cls, i) => {
    const last = segments[segments.length - 1];
    if (last && last.cls === cls) last.end = i + 1;
    else segments.push({ cls, start: i + 1, end: i + 1 });
  });
  return segments;
}
function round3(value) {
  return Math.round(value * 1e3) / 1e3;
}

// src/candidates.ts
var MANUALISH = ["owner-manual", "install-guide", "service-manual"];
function isManualishClassification(classification) {
  return MANUALISH.includes(classification);
}
function isManualWin(verdict, minConfidence) {
  return verdict.identityMatch === "yes" && isManualishClassification(verdict.classification) && verdict.publisher !== "third-party" && verdict.confidence >= minConfidence;
}
function suggestedTags(verdict) {
  const publishable = new Set(ModelDocTagSchema2.options);
  const suggested = [verdict.classification, ...verdict.alsoCovers ?? []].filter(
    (c) => publishable.has(c)
  );
  return [...new Set(suggested)];
}
function isVetoed(candidate) {
  return candidate?.disposition === "not-relevant";
}
function isReviewableCandidate(candidate) {
  return !!candidate.stagedStorageUrl && !candidate.oversize && !isVetoed(candidate);
}
function verdictCurrent(candidate, fingerprint) {
  return candidate.verdict.claimFingerprint === fingerprint;
}
function isConclusiveVerdict(candidate) {
  return !!candidate.verdict.verifiedVia;
}
function newCandidate(input, now) {
  const stagedStoragePath = input.stagedStorageUrl ? storagePathFromUrl(input.stagedStorageUrl) : void 0;
  return {
    object: "model-document-candidate",
    schema: 1,
    id: input.id,
    modelId: input.modelId,
    urls: [input.url],
    ...input.linkedFrom ? { linkedFrom: [input.linkedFrom] } : {},
    ...input.stagedStorageUrl ? { stagedStorageUrl: input.stagedStorageUrl } : {},
    ...stagedStoragePath ? { stagedStoragePath } : {},
    ...input.byteSize !== void 0 ? { byteSize: input.byteSize } : {},
    ...input.oversize ? { oversize: true } : {},
    verdict: input.verdict,
    ...input.gate ? { gate: input.gate } : {},
    ...input.trail ? { trails: [input.trail] } : {},
    triedAt: now,
    createdAt: now,
    updatedAt: now
  };
}
function withCandidateTrim(candidate, storage, keepRanges, byteSize, now) {
  assertTrimHasSource(candidate.trim);
  const sourcePath = candidate.trim?.sourcePath ?? candidateStagedPath(candidate);
  if (!sourcePath) throw new Error(`candidate ${candidate.id} has no staged copy to cut from`);
  return {
    ...candidate,
    stagedStorageUrl: storage.url,
    stagedStoragePath: storage.path,
    byteSize,
    trim: { sourcePath, keepRanges, trimmedAt: now },
    updatedAt: now
  };
}
function withoutCandidateTrim(candidate, storage, byteSize, now) {
  const { trim: _restored, byteSize: _stale, ...rest } = candidate;
  return {
    ...rest,
    stagedStorageUrl: storage.url,
    stagedStoragePath: storage.path,
    ...byteSize === void 0 ? {} : { byteSize },
    updatedAt: now
  };
}
function candidateStagedPath(candidate) {
  if (candidate.stagedStoragePath) return candidate.stagedStoragePath;
  return candidate.stagedStorageUrl ? storagePathFromUrl(candidate.stagedStorageUrl) : void 0;
}
function mergeCandidateObservation(existing, observed, now) {
  const { gate: priorGate, ...rest } = existing;
  const gate = observed.gate ?? (observed.verdict ? void 0 : priorGate);
  const linkedFrom = mergeLinkedFrom(existing.linkedFrom, observed.linkedFrom);
  const trails = mergeTrails(existing.trails, observed.trail);
  return {
    ...rest,
    urls: existing.urls.includes(observed.url) ? existing.urls : [...existing.urls, observed.url],
    ...linkedFrom ? { linkedFrom } : {},
    verdict: observed.verdict ?? existing.verdict,
    ...gate ? { gate } : {},
    ...trails ? { trails } : {},
    updatedAt: now
  };
}
var MAX_LINKED_FROM = 10;
function mergeLinkedFrom(prior, page) {
  if (!page || (prior ?? []).includes(page)) return prior;
  return [...prior ?? [], page].slice(0, MAX_LINKED_FROM);
}
var MAX_TRAILS = 3;
function mergeTrails(prior, next) {
  if (!next) return prior;
  const keep = (prior ?? []).filter((trail) => trailPathKey(trail) !== trailPathKey(next));
  return [next, ...keep].slice(0, MAX_TRAILS);
}
function trailPathKey(trail) {
  const search = trail.steps.find((step) => step.kind === "search");
  const doc = [...trail.steps].reverse().find((step) => step.kind === "document");
  return `${search?.kind === "search" ? search.query : ""}|${doc?.kind === "document" ? doc.url : ""}`;
}
function withDisposition(candidate, disposition, now, publishedDocId) {
  const { disposition: _oldDisposition, publishedDocId: _oldPublished, ...rest } = candidate;
  return {
    ...rest,
    ...disposition ? { disposition } : {},
    ...publishedDocId ? { publishedDocId } : {},
    updatedAt: now
  };
}

// src/coverage.ts
import { normalizeIdentifier as normalizeIdentifier2, typeAffinity } from "@rock/util";
var MIN_COVERAGE_PREFIX_LENGTH = 2;
var COVERAGE_BUCKET_LENGTH = 12;
function normalizeCoveragePrefix(raw) {
  return normalizeIdentifier2(raw);
}
function assertValidDocumentScope(scope) {
  if (scope.brandIds.length === 0) throw new Error("a document needs at least one brand");
  if (scope.typeIds.length === 0) throw new Error("a document needs at least one equipment type");
}
function assertValidCoverageRule(rule) {
  const ranges = rule.ranges ?? [];
  if (rule.prefixes.length === 0 && ranges.length === 0) {
    throw new Error("coverage needs at least one prefix or range");
  }
  for (const prefix of [...rule.prefixes, ...ranges.map((range) => range.prefix)]) {
    if (prefix !== normalizeCoveragePrefix(prefix)) {
      throw new Error(`coverage prefix "${prefix}" must be uppercase letters/numbers only`);
    }
    if (prefix.length < MIN_COVERAGE_PREFIX_LENGTH) {
      throw new Error(`coverage prefix "${prefix}" is shorter than ${MIN_COVERAGE_PREFIX_LENGTH} characters`);
    }
  }
  for (const range of ranges) {
    if (!Number.isInteger(range.from) || !Number.isInteger(range.to) || range.from < 0) {
      throw new Error(`coverage range ${formatCoverageRange(range)} needs non-negative integer bounds`);
    }
    if (range.from > range.to) {
      throw new Error(`coverage range ${formatCoverageRange(range)} is backwards (from > to)`);
    }
    if (rule.prefixes.includes(range.prefix)) {
      throw new Error(
        `coverage prefix "${range.prefix}" already covers range ${formatCoverageRange(range)} \u2014 remove one`
      );
    }
  }
}
function formatCoverageRange(range) {
  const bound = (value) => `${value}`.padStart(range.digits ?? 0, "0");
  return `${range.prefix}${bound(range.from)}\u2013${range.prefix}${bound(range.to)}`;
}
function coverageRangeKey(range) {
  return `${range.prefix}:${range.from}-${range.to}:${range.digits ?? 0}`;
}
function normalizeCoverageDashes(raw) {
  return raw.replace(/[‐-―−－]/g, "-");
}
function coverageRangeFromEndpoints(fromRaw, toRaw) {
  const split = (raw) => /^([A-Z0-9]*?)(\d+)$/.exec(normalizeCoveragePrefix(raw));
  const left = split(fromRaw);
  const right = split(toRaw);
  if (!left || !right) return { ok: false, error: "Each endpoint must end in a number (e.g. KSM45 and KSM200)" };
  const [, leftStem = "", leftDigits = ""] = left;
  const [, rightStem = "", rightDigits = ""] = right;
  if (leftStem !== rightStem) {
    return {
      ok: false,
      error: `Endpoints must share a stem \u2014 \u201C${normalizeCoveragePrefix(fromRaw)}\u201D and \u201C${normalizeCoveragePrefix(toRaw)}\u201D don\u2019t`
    };
  }
  let prefix = leftStem;
  let fromDigits = leftDigits;
  let toDigits = rightDigits;
  let digits;
  if (prefix.length < MIN_COVERAGE_PREFIX_LENGTH) {
    if (fromDigits.length !== toDigits.length) {
      return { ok: false, error: "Numeric endpoints must have the same number of digits" };
    }
    let shared = 0;
    while (shared < fromDigits.length - 1 && fromDigits[shared] === toDigits[shared]) shared++;
    prefix += fromDigits.slice(0, shared);
    fromDigits = fromDigits.slice(shared);
    toDigits = toDigits.slice(shared);
    digits = fromDigits.length;
  }
  if (prefix.length < MIN_COVERAGE_PREFIX_LENGTH) {
    return {
      ok: false,
      error: `Endpoints need ${MIN_COVERAGE_PREFIX_LENGTH} leading characters in common to be indexed`
    };
  }
  const from = Number.parseInt(fromDigits, 10);
  const to = Number.parseInt(toDigits, 10);
  if (from > to) return { ok: false, error: "Range is backwards \u2014 the first endpoint must be the lower one" };
  return { ok: true, range: { prefix, from, to, ...digits && digits > 1 ? { digits } : {} } };
}
function coverageKey(brandId, bucket) {
  return `${brandId}_${bucket}`;
}
function coverageBucket(prefix) {
  return prefix.slice(0, COVERAGE_BUCKET_LENGTH);
}
function coverageLookupKeys(brandId, modelCanonical) {
  const max = Math.min(modelCanonical.length, COVERAGE_BUCKET_LENGTH);
  const keys = [];
  for (let length = MIN_COVERAGE_PREFIX_LENGTH; length <= max; length++) {
    keys.push(coverageKey(brandId, modelCanonical.slice(0, length)));
  }
  return keys;
}
function coverageAppliesToModel(doc, model, families) {
  if (!doc.coverage) return void 0;
  const affinity = typeAffinity(model.typeId, doc.typeIds, families);
  if (!affinity || !model.brandId || !doc.brandIds.includes(model.brandId)) return void 0;
  return coverageSpecificity(doc.coverage, model.modelCanonical) > 0 ? affinity : void 0;
}
function documentLinkFit(doc, model, families) {
  const affinity = typeAffinity(model.typeId, doc.typeIds, families);
  if (!affinity) return "other-type";
  if (model.brandId && !doc.brandIds.includes(model.brandId)) return "other-brand";
  return affinity;
}
function isMisfit(fit) {
  return fit === "other-type" || fit === "other-brand";
}
function planStaleCoverageLinks(model, entries, families) {
  return entries.filter((entry) => entry.link.via === "coverage" && !coverageAppliesToModel(entry.doc, model, families)).map((entry) => entry.doc.id);
}
function withoutLinks(entries, docIds) {
  if (docIds.length === 0) return entries;
  return entries.filter((entry) => !docIds.includes(entry.doc.id)).map((entry) => {
    if (!entry.link.supersededBy || !docIds.includes(entry.link.supersededBy)) return entry;
    const { supersededBy: _by, supersededVia: _via, ...link } = entry.link;
    return { ...entry, link };
  });
}
function reachChange(docId, before, after, linkedModels, families) {
  const change = { revoked: [], flagged: [] };
  for (const model of linkedModels) {
    const link = model.documents?.[docId];
    if (!link) continue;
    if (!isDirectLink(link)) {
      if (coverageAppliesToModel(before, model, families) && !coverageAppliesToModel(after, model, families)) {
        change.revoked.push(model);
      }
    } else if (!isMisfit(documentLinkFit(before, model, families)) && isMisfit(documentLinkFit(after, model, families))) {
      change.flagged.push(model);
    }
  }
  return change;
}
function coverageSpecificity(coverage, modelCanonical) {
  return bestMatchLength(coveragePredicates(coverage), modelCanonical);
}
function coveragePredicates(coverage) {
  return [
    ...coverage.prefixes.map((prefix) => ({ match: "prefix", prefix })),
    ...(coverage.ranges ?? []).map(
      ({ prefix, from, to, digits }) => ({
        match: "range",
        prefix,
        from,
        to,
        ...digits ? { digits } : {}
      })
    )
  ];
}
function bestMatchLength(predicates, modelCanonical) {
  return Math.max(0, ...predicates.map((predicate) => predicateMatchLength(predicate, modelCanonical)));
}
function predicateMatchLength(predicate, modelCanonical) {
  switch (predicate.match) {
    case "prefix":
      return modelCanonical.startsWith(predicate.prefix) ? predicate.prefix.length : 0;
    case "range":
      return rangeMatchLength(predicate, modelCanonical);
  }
}
function rangeMatchLength(range, modelCanonical) {
  if (!modelCanonical.startsWith(range.prefix)) return 0;
  const digits = /^\d+/.exec(modelCanonical.slice(range.prefix.length))?.[0];
  if (!digits) return 0;
  const value = Number.parseInt(digits, 10);
  const inBounds = (range.digits === void 0 || range.digits === digits.length) && value >= range.from && value <= range.to;
  return inBounds ? range.prefix.length + digits.length : 0;
}
function remapDocumentType(doc, fromTypeId, toTypeId) {
  if (!doc.typeIds.includes(fromTypeId)) return void 0;
  const typeIds = [...new Set(doc.typeIds.map((id) => id === fromTypeId ? toTypeId : id))];
  return { ...doc, typeIds };
}
function diffCoverageProjection(before, after) {
  const beforeKeys = coverageKeys(before);
  const afterKeys = coverageKeys(after);
  return {
    addKeys: [...afterKeys].filter((key) => !beforeKeys.has(key)),
    removeKeys: [...beforeKeys].filter((key) => !afterKeys.has(key))
  };
}
function coverageKeys(doc) {
  if (!doc?.coverage) return /* @__PURE__ */ new Set();
  const predicates = coveragePredicates(doc.coverage);
  return new Set(doc.brandIds.flatMap((brandId) => predicates.map((predicate) => predicateKey(brandId, predicate))));
}
function coverageProjectionEntries(doc, docId) {
  const entries = /* @__PURE__ */ new Map();
  if (!doc.coverage) return entries;
  for (const brandId of doc.brandIds) {
    for (const predicate of coveragePredicates(doc.coverage)) {
      const key = predicateKey(brandId, predicate);
      const predicates = [...entries.get(key)?.predicates ?? [], predicate];
      entries.set(key, { docId, typeIds: doc.typeIds, predicates });
    }
  }
  return entries;
}
function predicateKey(brandId, predicate) {
  return coverageKey(brandId, coverageBucket(predicate.prefix));
}
function documentReachKey(doc) {
  if (!doc?.coverage) return "";
  return [
    [...doc.brandIds].sort().join(","),
    [...doc.typeIds].sort().join(","),
    [...doc.coverage.prefixes].sort().join(","),
    // `coverageRangeKey`, not the display form — see its doc for why the label can't be an identity.
    (doc.coverage.ranges ?? []).map(coverageRangeKey).sort().join(",")
  ].join("|");
}
function withProjectionEntry(entries, docId, next) {
  const others = entries.filter((entry) => entry.docId !== docId);
  return next ? [...others, next] : others;
}
function matchCoverage(projections, model, families) {
  const byDoc = /* @__PURE__ */ new Map();
  const missed = /* @__PURE__ */ new Map();
  for (const projection of projections) {
    for (const entry of projection.entries) {
      const specificity = bestMatchLength(entry.predicates, model.modelCanonical);
      if (specificity === 0) continue;
      const affinity = typeAffinity(model.typeId, entry.typeIds, families);
      if (!affinity) {
        if (!byDoc.has(entry.docId)) missed.set(entry.docId, { docId: entry.docId, typeIds: entry.typeIds });
        continue;
      }
      missed.delete(entry.docId);
      const match = { docId: entry.docId, specificity, affinity };
      const existing = byDoc.get(entry.docId);
      if (!existing || coverageMatchRank(match) > coverageMatchRank(existing)) byDoc.set(entry.docId, match);
    }
  }
  return { matches: [...byDoc.values()], nearMisses: [...missed.values()] };
}
function coverageMatchRank(match) {
  return match.specificity * 2 + (match.affinity === "type" ? 1 : 0);
}
function coverageEligible(model) {
  return !!model.brandId && !model.archivedAt;
}

// src/supersession.ts
import {
  DOC_TAG_LABELS as DOC_TAG_LABELS2,
  METADATA_TAGS,
  ModelDocTagSchema as ModelDocTagSchema3
} from "@rock/types";
var MAX_REPLACEMENT_HOPS = 10;
function planSupersession(model, entries, docs, families) {
  const contestants = entries.filter((entry) => !isHumanMark(entry.link));
  const desired = /* @__PURE__ */ new Map();
  const links = new Map(entries.map((entry) => [entry.doc.id, entry.link]));
  const lookup = withEntryDocs(docs, entries);
  for (const entry of contestants) {
    const head = replacementHead(entry.doc.id, replacementChain(entry.doc.id, lookup), links);
    if (head === entry.doc.id || replacementChain(head, lookup).includes(entry.doc.id)) continue;
    const successor = lookup.get(head);
    if (!successor || !canSupersede(successor, entry.doc)) continue;
    desired.set(entry.doc.id, { supersededBy: head, via: "replaced" });
  }
  const ranked = contestants.filter((entry) => !desired.has(entry.doc.id)).map((entry) => ({ entry, rank: linkRank(entry, model, families) })).sort((a, b) => compareRanks(b.rank, a.rank));
  for (const { entry, rank } of ranked) {
    if (!isAppVisibleDocument(entry.doc)) continue;
    const winner = ranked.filter((other) => other.entry !== entry && !desired.has(other.entry.doc.id)).filter((other) => canSupersede(other.entry.doc, entry.doc) && outranks(other.rank, rank)).sort((a, b) => compareRanks(b.rank, a.rank) || newestFirst(a.entry, b.entry))[0];
    if (winner) desired.set(entry.doc.id, { supersededBy: winner.entry.doc.id, via: "rule" });
  }
  return contestants.flatMap((entry) => {
    const mark = desired.get(entry.doc.id);
    return hasSupersession(entry.link, mark) ? [] : [{ docId: entry.doc.id, mark }];
  });
}
function canSupersede(successor, doc) {
  const kind = leadKind(doc);
  return !kind || successor.tags.includes(kind);
}
function supersessionRefusal(successor, doc) {
  const kind = leadKind(doc);
  if (!kind || canSupersede(successor, doc)) return void 0;
  return `The replacement must also be tagged ${DOC_TAG_LABELS2[kind]}, the most important kind of the document it replaces.`;
}
function leadKind(doc) {
  return ModelDocTagSchema3.options.find((tag) => doc.tags.includes(tag) && !METADATA_TAGS.includes(tag));
}
function isHumanMark(link) {
  return !!link.supersededBy && !link.supersededVia;
}
function hasSupersession(link, mark) {
  return mark ? link.supersededBy === mark.supersededBy && link.supersededVia === mark.via : !link.supersededBy;
}
function withSupersession(link, mark) {
  const { supersededBy: _by, supersededVia: _via, ...rest } = link;
  return mark ? { ...rest, supersededBy: mark.supersededBy, supersededVia: mark.via } : rest;
}
function withSupersessionChanges(entries, changes) {
  if (changes.length === 0) return entries;
  const byDoc = new Map(changes.map((change) => [change.docId, change]));
  return entries.map((entry) => {
    const change = byDoc.get(entry.doc.id);
    return change ? { ...entry, link: withSupersession(entry.link, change.mark) } : entry;
  });
}
function missingSuccessors(docs, held) {
  const missing = /* @__PURE__ */ new Set();
  for (const doc of docs) {
    if (doc.replacedBy && !held.has(doc.replacedBy)) missing.add(doc.replacedBy);
  }
  return [...missing];
}
function newestReplacement(docId, docs) {
  return replacementChain(docId, docs).at(-1) ?? docId;
}
function assertCanReplace(doc, successor, docs) {
  if (doc.id === successor.id) throw new Error("A document cannot replace itself");
  if (replacementChain(successor.id, docs).includes(doc.id)) {
    throw new Error(`${successor.id} is already replaced by ${doc.id}, directly or through a later revision`);
  }
  const refusal = supersessionRefusal(successor, doc);
  if (refusal) throw new Error(refusal);
}
function replacementPreview(successor, linkedModels, families) {
  const preview = { moves: [], stays: [] };
  for (const model of linkedModels) {
    if (model.archivedAt) continue;
    const reaches = !!model.documents?.[successor.id] || !!coverageAppliesToModel(successor, model, families);
    (reaches ? preview.moves : preview.stays).push(model);
  }
  return preview;
}
function replacementChain(docId, docs) {
  const chain = [docId];
  let next = docs.get(docId)?.replacedBy;
  while (next && !chain.includes(next) && chain.length <= MAX_REPLACEMENT_HOPS) {
    chain.push(next);
    next = docs.get(next)?.replacedBy;
  }
  return chain;
}
function replacementHead(entryId, chain, links) {
  const newestLinked = (ids) => ids.filter((id) => links.has(id)).at(-1) ?? entryId;
  const newest = newestLinked(chain);
  const mark = links.get(newest);
  const kept = mark?.supersededBy && isHumanMark(mark) ? chain.indexOf(mark.supersededBy) : -1;
  return kept >= 0 && kept < chain.indexOf(newest) ? newestLinked(chain.slice(0, kept + 1)) : newest;
}
function linkRank(entry, model, families) {
  if (isDirectLink(entry.link)) return { direct: true, match: 0 };
  const affinity = coverageAppliesToModel(entry.doc, model, families);
  if (!affinity || !entry.doc.coverage) return { direct: false, match: 0 };
  const specificity = coverageSpecificity(entry.doc.coverage, model.modelCanonical);
  return { direct: false, match: coverageMatchRank({ specificity, affinity }) };
}
function outranks(a, b) {
  if (b.direct) return false;
  return a.direct || a.match > b.match;
}
function compareRanks(a, b) {
  return Number(a.direct) - Number(b.direct) || a.match - b.match;
}
function newestFirst(a, b) {
  return b.link.linkedAt.localeCompare(a.link.linkedAt) || a.doc.id.localeCompare(b.doc.id);
}
function withEntryDocs(docs, entries) {
  const all = new Map(docs);
  for (const entry of entries) all.set(entry.doc.id, entry.doc);
  return all;
}

// src/coverageLinks.ts
function linkPlanWrites(documents, plan) {
  const next = /* @__PURE__ */ new Map();
  for (const link of plan.links) {
    if (!documents?.[link.id]) next.set(link.id, link);
  }
  for (const change of plan.changes) {
    const current = next.get(change.docId) ?? documents?.[change.docId];
    if (!current || isHumanMark(current) || hasSupersession(current, change.mark)) continue;
    next.set(change.docId, withSupersession(current, change.mark));
  }
  return next;
}
function planCoverageLinks(model, entries, matches, docs, families, now, opts = {}) {
  const linked = new Set(entries.map((entry) => entry.doc.id));
  const fresh = matches.flatMap((match) => {
    const doc = docs.get(match.docId);
    if (!doc || linked.has(doc.id)) return [];
    const link = {
      id: doc.id,
      via: "coverage",
      linkedAt: now,
      ...opts.silent ? { publishedSilently: true } : {}
    };
    return [{ link, doc }];
  });
  const changes = planSupersession(model, [...entries, ...fresh], docs, families);
  const marks = new Map(changes.map((change) => [change.docId, change.mark]));
  return {
    links: fresh.map((entry) => withSupersession(entry.link, marks.get(entry.doc.id))),
    changes: changes.filter((change) => linked.has(change.docId))
  };
}

// src/decoy.ts
var DECOY_GENERATORS = ["serif pageplus"];
var TEXT_FIELDS = ["title", "subject", "keywords", "author"];
var MARKUP = /<\/?(div|span|p|br|hr|center|h[1-6]|b|i|u|strong|em|a|img|table|tbody|thead|tr|td|th|ul|ol|li|font|meta|body|html|head|title|style|script)(\s[^<>]*)?\/?>|&nbsp;/i;
function detectDecoySignals(info) {
  const signals = [];
  for (const field of TEXT_FIELDS) {
    const value = info[field];
    const hit = value?.match(MARKUP)?.[0];
    if (hit) {
      signals.push({ code: "markup-in-metadata", detail: `PDF ${field} contains markup ("${hit}")` });
      break;
    }
  }
  const generator = matchGenerator(info.creator) ?? matchGenerator(info.producer);
  if (generator) {
    signals.push({ code: "decoy-generator", detail: `produced by ${generator}` });
  }
  return signals;
}
function matchGenerator(value) {
  const folded = value?.toLowerCase();
  if (!folded) return void 0;
  return DECOY_GENERATORS.some((generator) => folded.includes(generator)) ? value : void 0;
}
function decoyReason(signals) {
  return `content-farm decoy: ${signals.map((s) => s.detail).join("; ")}`;
}

// src/docStatus.ts
function deriveModelDocStatus(model, entries, job) {
  if (model.archivedAt) return "archived";
  const ingestFailed = entries.some(
    (entry) => !isSupersededEntry(entry) && (entry.doc.ingest?.status === "failed" || entry.doc.ingest?.status === "unsupported")
  );
  if (ingestFailed) return "ingest-failed";
  if (hasAppVisibleDoc(entries)) return "published";
  const bucket = jobDocStatusBucket(job?.status);
  if (bucket === "needs-review" || bucket === "searching") return bucket;
  if (model.revisit) return "revisit";
  if (model.parked) return "parked";
  if (bucket === "search-failed") return "search-failed";
  if (entries.length > 0) return "library-only";
  return "missing";
}
function jobDocStatusBucket(status) {
  switch (status) {
    case "pending":
    case "running":
      return "searching";
    case "succeeded":
      return "needs-review";
    case "failed":
    case "unavailable":
    case "cancelled":
      return "search-failed";
    default:
      return "none";
  }
}
function manualHuntState(docStatus, entries) {
  if (hasAppVisibleDoc(entries)) return void 0;
  switch (docStatus) {
    case "searching":
    case "needs-review":
    case "missing":
      return "searching";
    case "search-failed":
    case "parked":
    case "revisit":
    case "library-only":
      return "not-found";
    default:
      return void 0;
  }
}
function modelDocStatusInputsKey(model) {
  const links = Object.values(model.documents ?? {}).filter((link) => !!link).map((link) => `${link.id}:${link.supersededBy ?? ""}`).sort().join("|");
  const parked = model.parked ? `${model.parked.reason}@${model.parked.at}` : "";
  const revisit = model.revisit ? `${model.revisit.reason}@${model.revisit.at}` : "";
  return `${model.archivedAt ?? ""}#${parked}#${revisit}#${links}`;
}
function documentDocStatusInputsKey(doc) {
  return `${doc.ingest?.status ?? ""}#${[...doc.tags].sort().join(",")}`;
}

// src/ingest.ts
import {
  DIGESTIBLE_TAGS
} from "@rock/types";
var INGEST_PENDING_STALE_MS = 10 * 60 * 1e3;
function isDigestibleDocument(doc) {
  return doc.tags.some((tag) => DIGESTIBLE_TAGS.includes(tag));
}
function canClaimDocumentIngest(doc, staleMs, now) {
  if (!doc || !isDigestibleDocument(doc)) return false;
  const ingest = doc.ingest;
  if (!ingest) return true;
  if (ingest.status !== "pending") return false;
  return Date.parse(ingest.at) + staleMs <= Date.parse(now);
}
function startDocumentIngest(doc, now) {
  return { ...doc, ingest: { status: "pending", at: now } };
}
function completeDocumentIngest(doc, result, now) {
  const digest = result.digest ?? doc.digest;
  return {
    ...doc,
    ...digest ? { digest } : {},
    ingest: {
      status: result.status,
      at: now,
      ...result.openaiFileId ? { openaiFileId: result.openaiFileId } : {}
    }
  };
}

// src/jobSnapshot.ts
import { isHttpUrl } from "@rock/util";
function manualJobSnapshot(model, entries, search) {
  const candidateUrls = [search?.offeredUrl, ...libraryDocuments(entries).map((entry) => entry.doc.sourceUrl)].filter(
    isHttpUrl
  );
  return {
    modelNumber: model.modelDisplay ?? model.modelCanonical,
    brandId: model.brandId,
    spaceId: model.spaceId,
    equipmentType: model.details?.type,
    ...candidateUrls.length ? { candidateUrls } : {},
    ...search ? { activeSearchId: search.id } : {}
  };
}

// src/knownAs.ts
import { normalizeIdentifier as normalizeIdentifier3 } from "@rock/util";
function mergeKnownAs(existing, incoming) {
  const keyOf = (k) => `${k.kind}:${normalizeIdentifier3(k.name)}`;
  const byKey = /* @__PURE__ */ new Map();
  for (const entry of existing ?? []) byKey.set(keyOf(entry), entry);
  for (const entry of incoming) {
    if (byKey.get(keyOf(entry))?.confirmed) continue;
    byKey.set(keyOf(entry), entry);
  }
  return [...byKey.values()];
}

// src/publish.ts
function publishDocumentFromCandidate(candidate, tags, now, opts) {
  if (!candidate.stagedStorageUrl) {
    throw new Error(`candidate ${candidate.id} has no staged bytes to publish`);
  }
  const scope = { brandIds: dedupe(opts.brandIds), typeIds: dedupe(opts.typeIds) };
  assertValidDocumentScope(scope);
  const storagePath = candidate.stagedStoragePath ?? storagePathFromUrl(candidate.stagedStorageUrl);
  return {
    object: "document",
    schema: 1,
    id: candidate.id,
    tags: dedupe(tags),
    ...opts.title ? { title: opts.title } : {},
    ...scope,
    ...storagePath ? { storagePath } : {},
    storageUrl: candidate.stagedStorageUrl,
    ...candidate.urls[0] ? { sourceUrl: candidate.urls[0] } : {},
    // A hand-cut candidate publishes as a cut document — the cut is a fact about the bytes, and the
    // pointer back to the full original comes with it, so the publish stays undoable.
    ...candidate.trim ? { trim: candidate.trim } : {},
    createdAt: now,
    updatedAt: now
  };
}
function directDocumentLink(docId, now, opts = {}) {
  return {
    id: docId,
    via: "direct",
    linkedAt: now,
    ...opts.notify === false ? { publishedSilently: true } : {}
  };
}
function withDocumentTags(existing, tags, now) {
  return { ...existing, tags: dedupe(tags), updatedAt: now };
}
function withMergedTags(existing, tags, now) {
  return withDocumentTags(existing, [...existing.tags, ...tags], now);
}
function withMergedPublish(existing, publish, now) {
  const scope = {
    brandIds: [...existing.brandIds, ...publish.brandIds],
    typeIds: [...existing.typeIds, ...publish.typeIds]
  };
  return withDocumentScope(withMergedTags(existing, publish.tags, now), scope, now);
}
function withDocumentScope(existing, scope, now) {
  const next = { brandIds: dedupe(scope.brandIds), typeIds: dedupe(scope.typeIds) };
  assertValidDocumentScope(next);
  return { ...existing, ...next, updatedAt: now };
}
function withDocumentTrim(doc, storage, keepRanges, now) {
  assertTrimHasSource(doc.trim);
  const sourcePath = doc.trim?.sourcePath ?? documentStoragePath(doc);
  if (!sourcePath) throw new Error(`document ${doc.id} has no stored copy to cut from`);
  const { ingest: _replaced, ...rest } = doc;
  return {
    ...rest,
    storagePath: storage.path,
    storageUrl: storage.url,
    trim: { sourcePath, keepRanges, trimmedAt: now },
    updatedAt: now
  };
}
function withoutDocumentTrim(doc, storage, now) {
  const { ingest: _replaced, trim: _restored, ...rest } = doc;
  return { ...rest, storagePath: storage.path, storageUrl: storage.url, updatedAt: now };
}
function assertCanSupersede(entries, docId, successorId) {
  if (docId === successorId) throw new Error("a document cannot supersede itself");
  const superseded = entries.find((entry) => entry.doc.id === docId);
  if (!superseded) throw new Error(`document ${docId} is not confirmed on this model`);
  const successor = entries.find((entry) => entry.doc.id === successorId);
  if (!successor) throw new Error(`successor ${successorId} is not confirmed on this model`);
  if (successor.link.supersededBy) {
    throw new Error("the replacing document is itself superseded \u2014 pick the current one");
  }
  const refusal = supersessionRefusal(successor.doc, superseded.doc);
  if (refusal) throw new Error(refusal);
}
function dedupe(values) {
  return [...new Set(values)];
}

// src/reviewState.ts
function planReviewState(model, entries, job) {
  const consumed = !!model.archivedAt || hasAppVisibleDoc(entries);
  return {
    clearRevisit: consumed && !!model.revisit,
    job: consumed && job?.status === "succeeded" ? "mark-reviewed" : !consumed && job?.status === "reviewed" ? "reopen" : void 0
  };
}

// src/revisit.ts
function planModelRevisit(model, entries, job, reason, now) {
  if (model.archivedAt) return void 0;
  if (hasAppVisibleDoc(entries)) return void 0;
  if (model.revisit?.reason === reason) return void 0;
  const revisit = { reason, at: now };
  if (model.parked) {
    return { revisit, requestSearch: searchWorthwhile(model.parked.reason, reason) };
  }
  if (jobGaveUp(job)) {
    return { revisit, requestSearch: true };
  }
  return void 0;
}
function mayRevisit(model) {
  if (model.archivedAt) return false;
  return !!model.parked || !!model.revisit || model.docStatus === "search-failed";
}
function searchWorthwhile(park, signal) {
  switch (park) {
    case "cant-find":
      return true;
    case "unidentified":
      return signal === "brand-linked";
    case "no-manual-exists":
      return false;
  }
}
function jobGaveUp(job) {
  return job?.status === "failed" || job?.status === "cancelled" || job?.status === "unavailable";
}

// src/search.ts
function newDocumentSearch(id, modelId, now, opts = {}) {
  return {
    object: "document-search",
    schema: 1,
    id,
    modelId,
    requestedAt: now,
    ...opts.offeredUrl ? { offeredUrl: opts.offeredUrl } : {},
    ...opts.allowOversize && opts.offeredUrl ? { allowOversize: true } : {},
    ...opts.origin ? { origin: opts.origin } : {},
    ...opts.replaces && opts.offeredUrl ? { replaces: opts.replaces } : {},
    status: "requested",
    attempts: 0,
    createdAt: now,
    updatedAt: now
  };
}
function searchRunning(search, attempts, now) {
  return { ...search, status: "running", attempts, updatedAt: now };
}
function searchPending(search, attempts, lastError, now) {
  return { ...search, status: "requested", attempts, lastError, updatedAt: now };
}
function searchOfferJudged(search, outcome, note, now) {
  if (search.offerOutcome === "candidate" && outcome === "none") return search;
  if (search.offerOutcome === outcome && search.offerNote === note) return search;
  return { ...search, offerOutcome: outcome, offerNote: note, updatedAt: now };
}
function searchFinished(search, outcome, now, lastError) {
  const { lastError: _stale, ...rest } = search;
  return {
    ...rest,
    status: outcome,
    ...lastError ? { lastError } : {},
    finishedAt: now,
    updatedAt: now
  };
}

// src/searchable.ts
import { isMeaningfulBrandName } from "@rock/util";
var MIN_DISTINCTIVE_CANONICAL_LENGTH = 5;
function unsearchableReason(input) {
  if (isMeaningfulBrandName(input.brand)) return void 0;
  if (input.identities.some((name) => name.trim().length > 0)) return void 0;
  const canonical = input.modelCanonical.trim();
  if (/^\d+$/.test(canonical)) {
    return "no brand, and the model number is only digits \u2014 nothing names the product";
  }
  if (canonical.length < MIN_DISTINCTIVE_CANONICAL_LENGTH) {
    return "no brand, and the model number is too short to identify a product";
  }
  return void 0;
}

// src/silenceDocumentLinks.ts
function silenceDocumentLinks(documents) {
  if (!documents) return documents;
  return Object.fromEntries(
    Object.entries(documents).map(([hash, link]) => [hash, link ? { ...link, publishedSilently: true } : link])
  );
}

// src/storageUrl.ts
var STORAGE_HOST = "firebasestorage.googleapis.com";
function assertOurManualStorageUrl(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("manual.storageUrl is not a valid URL");
  }
  const { host, protocol } = parsed;
  if (protocol !== "https:" || host !== STORAGE_HOST && !DOCS_HOSTS.includes(host)) {
    throw new Error(`manual.storageUrl host not allowed: ${host}`);
  }
  const decoded = DOCS_HOSTS.includes(host) ? decodeURIComponent(parsed.pathname.slice(1)) : decodeURIComponent(url.match(/\/o\/([^?]+)/)?.[1] ?? "");
  if (!decoded.startsWith("models/")) {
    throw new Error(`manual.storageUrl path not allowed: ${decoded}`);
  }
}
function isOurManualStorageUrl(url) {
  try {
    assertOurManualStorageUrl(url);
    return true;
  } catch {
    return false;
  }
}
export {
  AUTO_CONFIRM_GATE_MIN_CONFIDENCE,
  COVERAGE_BUCKET_LENGTH,
  DECOY_GENERATORS,
  DOCS_HOSTS,
  GATE_REVISION,
  INGEST_PENDING_STALE_MS,
  MAX_REPLACEMENT_HOPS,
  MIN_COVERAGE_PREFIX_LENGTH,
  TRIM_ENGINE,
  TRIM_MAX_ENGLISH_SECTIONS,
  TRIM_MIN_LETTERS,
  TRIM_MIN_PROB,
  TRIM_REVISION,
  TRUSTED_DOCUMENT_HOSTS,
  ambientHuntBlockedReason,
  appVisibleDocuments,
  appliedTrimDecision,
  assertCanReplace,
  assertCanSupersede,
  assertCanTrimDocument,
  assertModelStoragePath,
  assertOurManualStorageUrl,
  assertTrimHasSource,
  assertValidCoverageRule,
  assertValidDocumentScope,
  assertValidPageRanges,
  buildDocumentLanguageAnalysis,
  canClaimDocumentIngest,
  canSupersede,
  candidateStagedPath,
  candidateStoragePath,
  classifyTrimPage,
  collapseToRanges,
  completeDocumentIngest,
  composeKeepRanges,
  computeTrimProposal,
  countPagesInRanges,
  coverageAppliesToModel,
  coverageBucket,
  coverageEligible,
  coverageKey,
  coverageLookupKeys,
  coverageMatchRank,
  coverageProjectionEntries,
  coverageRangeFromEndpoints,
  coverageRangeKey,
  coverageSpecificity,
  decoyReason,
  deriveModelDocStatus,
  detectDecoySignals,
  diffCoverageProjection,
  directDocumentLink,
  docsBaseUrlForProject,
  docsBucketNameForProject,
  documentDocStatusInputsKey,
  documentEntries,
  documentLinkFit,
  documentLinks,
  documentReachKey,
  documentStoragePath,
  documentTitle,
  documentsTagged,
  evaluateAutoConfirmGate,
  formatCoverageRange,
  formatPageRanges,
  hasAppVisibleDoc,
  hasManual,
  hasSupersession,
  includesAppVisibleTag,
  invertPageRanges,
  isAppVisibleDocument,
  isConclusiveVerdict,
  isDigestibleDocument,
  isDirectLink,
  isHumanMark,
  isManualConfirmed,
  isManualWin,
  isManualishClassification,
  isMisfit,
  isOurManualStorageUrl,
  isReviewableCandidate,
  isSupersededEntry,
  isUploadedSource,
  isVetoed,
  jobDocStatusBucket,
  leadKind,
  libraryDocuments,
  linkPlanWrites,
  linkedDocumentIds,
  manualHuntState,
  manualJobSnapshot,
  manualUrl,
  matchCoverage,
  mayRevisit,
  mergeCandidateObservation,
  mergeKnownAs,
  mergeLinkedFrom,
  mergeTrails,
  missingSuccessors,
  modelDocStatusInputsKey,
  newCandidate,
  newDocumentSearch,
  newestReplacement,
  newlyLinkedDocumentIds,
  normalizeCoverageDashes,
  normalizeCoveragePrefix,
  offerStoragePath,
  ownerManualDocument,
  pagesInRanges,
  parsePageRanges,
  planCoverageLinks,
  planDocumentMirror,
  planModelRevisit,
  planReviewState,
  planStaleCoverageLinks,
  planSupersession,
  primaryDocumentTagged,
  publishDocumentFromCandidate,
  rangesEqual,
  reachChange,
  remapDocumentType,
  replacementChain,
  replacementPreview,
  resolveTrimKeepRanges,
  searchFinished,
  searchOfferJudged,
  searchPending,
  searchRunning,
  servedDocumentUrl,
  silenceDocumentLinks,
  startDocumentIngest,
  storagePathFromUrl,
  suggestedTags,
  summarizeLanguagePages,
  supersessionRefusal,
  trimmedStoragePath,
  uncertainTrimPages,
  unsearchableReason,
  verdictCurrent,
  withCandidateTrim,
  withDisposition,
  withDocumentScope,
  withDocumentTags,
  withDocumentTrim,
  withMergedPublish,
  withMergedTags,
  withProjectionEntry,
  withSupersession,
  withSupersessionChanges,
  withoutCandidateTrim,
  withoutDocumentTrim,
  withoutLinks
};
//# sourceMappingURL=index.js.map